import React from 'react'
import { useParams } from 'react-router-dom'
import { usePodcast } from '../../../api'
import { useVoices } from '../../../api/voices'
import { useEditorStore } from '../state/editor-store'
import { CompletePodcastEditorSkeleton } from '../components/skeletons'
import { useSignalRConnection } from '../hooks'
import { useVoicesInitialization } from './voices-initialization'
import { useContextActions } from './context-actions'
import { ProviderErrorState } from './provider-error-state'
import { PodcastEditorContext, type PodcastEditorContextType } from './podcast-editor-context'

import { addRecentEpisode } from '../../../lib/recent-episodes'
import { useScriptSuggestionsStore } from '../state/script-suggestions-store'

export type VoiceOption = {
  id: string
  name?: string | null
  description?: string | null
  gender?: string | null
  previewUrl?: string | null
  category?: string
  isDefaultA?: boolean
  isDefaultB?: boolean
}

export const PodcastEditorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { id = '' } = useParams<{ id: string }>()
  const { data: podcast, isLoading, isError, refetch } = usePodcast(id)

  // Filter voices by podcast language for better UX
  const podcastLanguage = podcast?.languageIso
  const { data: allVoices, isLoading: loadingVoices, isError: voicesError, refetch: refetchVoices } = useVoices(undefined, podcastLanguage)

  const hostA = podcast?.hostAVoiceId || 'c961b81c-a935-4c17-bfb3-ba2239de8c2f'
  const hostB = podcast?.hostBVoiceId || '6ccbfb76-1fc6-48f7-b71d-91ac6298247b'

  // Wrap refetch functions to match expected signature
  const wrappedRefetch = React.useCallback(async () => {
    await refetch()
  }, [refetch])

  const wrappedRefetchVoices = React.useCallback(async () => {
    await refetchVoices()
  }, [refetchVoices])

  // Use extracted hooks
  useSignalRConnection(id, wrappedRefetch)
  useVoicesInitialization(allVoices, hostA, hostB)

  const actions = useContextActions(null, wrappedRefetch, wrappedRefetchVoices, id)

  // Prime store when data loads
  React.useEffect(() => {
    if (!podcast) return
    useEditorStore.getState().setPodcast(podcast)
    // Record recent episode visit when loaded
    addRecentEpisode({ id: podcast.id, title: podcast.title })
    useScriptSuggestionsStore.getState().hydrate(podcast.id)
  }, [podcast])

  const statusLoading = isLoading || loadingVoices
  const statusError = isError || voicesError || !podcast

  // derive context values from store
  const store = useEditorStore()
  const loadingActivity = useEditorStore((s) => s.activity)
  const isGeneratingAudio = loadingActivity?.phase === 'synthesis' && loadingActivity.step !== 'line_completed';
  const isGeneratingScript = loadingActivity?.phase === 'script'

  const value: PodcastEditorContextType | null = !statusLoading && !statusError && podcast ? {
    id,
    podcast: store.podcast!,
    isLoading: statusLoading,
    isError: !!statusError,
    voices: store.voices,
    hostAVoiceId: store.hostAVoiceId,
    hostBVoiceId: store.hostBVoiceId,
    editingTitle: store.editingTitle,
    titleDraft: store.titleDraft,
    speed: store.speed,
    volume: store.volume,
    emotion: store.emotion,
    languageIso: store.languageIso,
    loadingActivity,
    isGeneratingAudio,
    isGeneratingScript,
    ...actions,
  } : null

  const handleRetry = React.useCallback(() => {
    void Promise.all([refetch(), refetchVoices()])
  }, [refetch, refetchVoices])

  return (
    <PodcastEditorContext.Provider value={value}>
      {statusLoading ? (
        <CompletePodcastEditorSkeleton />
      ) : statusError ? (
        <ProviderErrorState onRetry={handleRetry} />
      ) : (
        children
      )}
    </PodcastEditorContext.Provider>
  )
}